import { z } from "zod";

/**
 * Wire-level profile object nested inside brand-profile responses.
 * Returned by both /extract and /status on success. Nullable in the
 * status route when no brand config row exists yet.
 */
const BrandProfile = z.object({
  id: z.string(),
  projectId: z.string(),
  brandName: z.string(),
  brandNameVariations: z.array(z.string()),
  brandUrl: z.string(),
  industry: z.string(),
  businessType: z.string().nullable(),
  pricingModel: z.string().nullable(),
  products: z.array(z.string()),
  features: z.array(z.string()),
  useCases: z.array(z.string()),
  personas: z.array(z.string()),
  targetLocations: z.array(z.string()),
  competitors: z.array(z.string()),
  integrationsOrBrandsCarried: z.array(z.string()),
  targetPersona: z.string(),
  keywords: z.array(z.string()),
  signals: z.record(z.unknown()),
  source: z.string(),
  extractionStatus: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/**
 * Wire-level response for POST /api/visibility/brand-profile/extract.
 * The route returns this on success after starting the Temporal
 * brandProfileWorkflow.
 */
export const BrandProfileExtractResponse = z.object({
  profileId: z.string(),
  workflowId: z.string(),
  status: z.literal("extracting"),
});
export type BrandProfileExtractResponse = z.infer<
  typeof BrandProfileExtractResponse
>;

/**
 * Wire-level response for GET /api/visibility/brand-profile/status.
 * All branches share this flat schema; `profile` is null when no
 * brand config row exists yet. Extracting branches may omit
 * `temporalUnavailable`.
 */
export const BrandProfileStatusResponse = z.object({
  status: z.string(),
  profile: BrandProfile.nullable().optional(),
  error: z.string().optional(),
  temporalUnavailable: z.boolean().optional(),
});
export type BrandProfileStatusResponse = z.infer<
  typeof BrandProfileStatusResponse
>;

/**
 * Wire-level response for POST /api/visibility/brand-profile/confirm.
 * The route returns a minimal confirmation envelope. The CLI discards
 * this value but we assert the shape so a server-side contract change
 * fails fast at the boundary.
 */
export const BrandProfileConfirmResponse = z.object({
  success: z.boolean(),
  status: z.string(),
});
export type BrandProfileConfirmResponse = z.infer<
  typeof BrandProfileConfirmResponse
>;
